<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
const logger = useLogger()
import type { IAoeCartItem, IAoeCartEarnedDiscount } from '@/types/CartTypes.ts'
import {} from '@/services/CartService.ts'
import { SvgIcon } from '@progress/kendo-vue-common'
import { checkOutlineIcon, plusCircleIcon } from '@progress/kendo-svg-icons'
import { FormatCurrency } from '@/services/currencyFormatter'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { computed } from 'vue'
import type { IAoeCart } from '@/types/CartTypes.ts'
import {
  AddDiscountedItemToCart,
  DeleteItemFromCart,
} from '@/services/CartService.ts'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import { handleError } from '@/services/ErrorService.ts'

const props = defineProps<{
  cart: IAoeCart
  earnedDiscount: IAoeCartEarnedDiscount
  GetFullProductImageUrlFromStringDictionary(productCode: string): string
  FindCartItems(poeDiscPlanLevelDiscId: number): IAoeCartItem[] | null
}>()

const emit = defineEmits(['update-cart'])

logger.debug('AoeCartDetailDiscountedItem props:', props)

const discountedItem = computed(() => {
  let discountedItem = null
  const discountedItems = props.FindCartItems(
    props.earnedDiscount.poeDiscPlanLevelDiscId,
  )
  if (discountedItems && discountedItems.length > 0) {
    discountedItem = discountedItems[0]
  }
  logger.debug('discountedItem:', discountedItem)
  return discountedItem
})

const itemToSelect = computed(() => {
  let itemToSelect = null
  if (
    discountedItem.value == null &&
    props.earnedDiscount &&
    props.earnedDiscount.items &&
    props.earnedDiscount.items?.length > 0
  ) {
    itemToSelect = props.earnedDiscount.items[0]
  }
  logger.debug('itemToSelect:', itemToSelect)
  return itemToSelect
})

const deleteIsVisible = computed(() => {
  let deleteIsVisible = true
  if (
    discountedItem.value != null &&
    props.earnedDiscount &&
    props.earnedDiscount.parameters &&
    props.earnedDiscount.parameters.AutoSelected == 'true'
  ) {
    deleteIsVisible = false
  }
  logger.debug('deleteIsVisible:', deleteIsVisible)
  return deleteIsVisible
})

async function AddDiscountedItemClickedHandler(poeDiscPlanLevelDiscId: number) {
  logger.debug('AddDiscountedItemClickedHandler')
  if (itemToSelect.value == null) {
    return
  }
  SetIsLoading(true)
  AddDiscountedItemToCart(
    props.cart.aoeCartIdToken,
    {
      skuCode: itemToSelect.value.skuCode,
      quantity: 1,
      poeDiscPlanLevelDiscId,
    },
    undefined,
  )
    .catch(async error => {
      await handleError(error, t)
    })
    .finally(() => {
      SetIsLoading(false)
      emit('update-cart', null)
    })
}
function deleteItemClickedHandler(lineNumber: number) {
  logger.debug('deleteItemClickedHandler', lineNumber)

  SetIsLoading(true)
  DeleteItemFromCart({
    aoeCartIdToken: props.cart ? props.cart.aoeCartIdToken : '',
    lineNumber,
  }).then(() => {
    logger.debug('DeleteItemFromCart success')
    SetIsLoading(false)
    emit('update-cart', event)
  })
}
</script>
<template>
  <div v-if="discountedItem" class="row discounted-item-row">
    <div class="col-1 discounted-item-chosen-check">
      <SvgIcon class="check-mark-outline-icon" :icon="checkOutlineIcon">
      </SvgIcon>
    </div>
    <div
      class="col-2 discountMessage discounted-item-message"
      v-html="props.earnedDiscount.awardMessage"
    ></div>
    <div class="col-1 discounted-item-chosen-image">
      <img
        :src="
          props.GetFullProductImageUrlFromStringDictionary(
            discountedItem.productCode,
          )
        "
        class="img-fluid prodImage"
        :alt="t('cart.ProductImageAlt')"
      />
    </div>
    <div class="col-7 discounted-item-chosen-name">
      {{ discountedItem.skuName
      }}<span class="strike-through cart-item-retail-price">{{
        FormatCurrency(discountedItem.price, null)
      }}</span
      ><span class="cart-item-price">{{
        FormatCurrency(
          discountedItem.totalDiscountAmount
            ? discountedItem.price - discountedItem.totalDiscountAmount
            : discountedItem.price,
          null,
        )
      }}</span>
    </div>
    <div class="col-1 discounted-item-delete" v-if="deleteIsVisible">
      <KButton
        :fill-mode="'clear'"
        @click="deleteItemClickedHandler(discountedItem.lineNumber)"
        class="deleteItemButton"
        >&#x2715;</KButton
      >
    </div>
  </div>
  <div v-if="itemToSelect" class="row discounted-item-row">
    <div class="col-1 discounted-item-chosen-check">
      <KButton
        :svg-icon="plusCircleIcon"
        :fill-mode="'link'"
        class="plus-circle-button"
        @click="
          AddDiscountedItemClickedHandler(earnedDiscount.poeDiscPlanLevelDiscId)
        "
      ></KButton>
    </div>
    <div
      class="col-2 discountMessage discounted-item-message"
      v-html="props.earnedDiscount.awardMessage"
    ></div>
    <div class="col-1 discounted-item-chosen-image">
      <img
        :src="
          props.GetFullProductImageUrlFromStringDictionary(
            itemToSelect.productCode,
          )
        "
        class="img-fluid prodImage"
        :alt="t('cart.ProductImageAlt')"
      />
    </div>
    <div class="col-6 discounted-item-chosen-name">
      {{ itemToSelect.skuName }}
    </div>
  </div>
</template>

<style lang="scss">
.discounted-item-chosen-check,
.discounted-item-chosen-message,
.discounted-item-chosen-name,
.discounted-item-delete {
  align-content: center;
}

.discounted-item-chosen-check .k-button-icon.k-svg-icon > svg {
  width: 24px !important;
  height: 24px !important;
  color: var(--standardColor) !important;
}
.discounted-item-chosen-check .k-button {
  padding-left: 0px !important;
}
</style>
